// Overhead
import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import _get from 'lodash/get';

// Components
import LinnerDiv from '../../components/lineDiv';
import Note from '../../components/note';
import ServerErrors from '../../components/ServerErrors';
import AmountWrapper from '../../components/amountWrapper';
import GuestForm from '../guest/guestForm';
import PayCreditOrDebit from '../../components/payCreditOrDebit';
import RecurringCheckBox from '../../components/recurring-checkbox/recurring-checkbox';
import DonateHeader from '../../components/common/DonateHeader';
import CloseButton from '../../components/CloseButton';
import { Divider } from '@paypalcorp/pp-react-divider';
import { Alert } from '@paypalcorp/pp-react-alert';

// App Store/Flux
import AppStore from '../../stores/AppStore';
import AppDispatcher from '../../dispatcher/AppDispatcher';
import * as DonateAppActions from '../../actions/AppActions';
import * as AppConstants from '../../utility/constants';
// Utility
import '../../utility/csrf';
import * as amountUtils from '../../components/amount/amountUtils';
import {
  handleLogIn,
  handleTokenReuse,
  addShake,
  updateDonation,
  observeUntilEventObserved,
  getInitialDataInDom,
} from '../../utility/helper';
import * as FPT from '../../utility/fpt';
import templateData, { localsMessages } from '../../utility/getTemplateData';
import {
  formatAmount,
  normalizeNumber,
  formatCurrency,
} from '../../utility/formator';
import DonateGiftAid from '../../guest-payment-components/DonateGiftAid';
import withDonate from '../../components/guest/WithDonate';
// import TeslaFormContainer from './teslaFormContainer';
import * as cpl from '../../utility/cpl';

import CancelAndReturn from '../../components/cancelAndReturn';
import Disclaimer from '../../components/Disclaimer';
import { PPGF_DISCLAIMER_TOUCHPOINT } from '../../utility/constants';
import {
  getPersonalizedMessages,
  hasPersonalizedMessages,
} from '../../utility/personalizedMessages';
import {
  scrollElementIntoView,
  objectOrArrayToQueryString,
} from '../../utility/domUtils';
import getContent from 'pp-react-l10n';
import { isExternalFlow } from '../../utility/externalIntegrationUtils';
import {
  isInContext,
  isMobileOrInContext,
} from '../../utility/inContextDonation';
import BackButton from '../../components/BackButton';
import isNna from '../../utility/nna';
import { reactLazyPreload } from '../../utility/lazyLoader';
import FallbackSpinner from '../common/FallbackSpinner';
import { getParameterByName } from '../../utility/queryStringHelper';
import { isCrsAborted } from '../../utility/crsUtils';

const messages = localsMessages('common/amount');
const auCrsMessages = localsMessages('guest/guestForm');

let initialData = getInitialDataInDom();

let giftAidText = localsMessages('common/giftAidIt');
let giftAidTextObj = (giftAidText && giftAidText.giftAidIt) || {};
const donateGiftPercentage = AppStore.getGiftAidItExtraAmount();

const TeslaFormContainer = reactLazyPreload(() =>
  import(/* webpackChunkName: "TeslaFormContainer" */ './teslaFormContainer')
);

class Guest extends React.Component {
  constructor(props) {
    super(props);

    this.props.showErrorOnLoad();

    this.state = {
      formattedAmount: this.getFormattedAmount(),
      hasLogo: templateData.hasLogo,
      subHeaderLabel: templateData.donationName,
      orgName: templateData.charityName,
      donationId: templateData.donationId,
      guestRedirectUrl: templateData.guestRedirectUrl,
      donateDisabled: false,
      showServiceErrors: false,
      errorMessages: [],
      recurringEnabled: templateData.recurringEnabled,
      recurringStatus: false,
      country: initialData.selectedCountry,
      selectedCountry: initialData.selectedCountry,
      memberMandatory: templateData.memberMandatory || false,
      accountCreationTriggered: false,

      recurringHelpText: '',
      recurringLabel: 'Make this a monthly donation',
      isProgressive:
        (templateData.onboardingEnabled &&
          initialData.selectedCountry !== 'RU') ||
        false,
      isDonating: false,
      isShipping: templateData.isShipping || false,
      shareAddress: {},
      progressiveComponentLoaded: false,
      paymentProcessing: false,
      programNames: templateData.donateContext.program_names,
      hasDisclaimer: hasPersonalizedMessages(PPGF_DISCLAIMER_TOUCHPOINT),
      showAuCrsBanner: false,
    };
  }

  getFormattedAmount = () => {
    const currency = AppStore.getDonationCode() || '';
    const amount = AppStore.getDonationAmount();
    let formattedAmount = '';
    if (amount) {
      formattedAmount = formatAmount({ currency, value: amount });
    }
    return formattedAmount;
  };

  logCPLData = (logData) => {
    const spinnerOverlay = document.getElementById('spinnerOverlay');

    if (spinnerOverlay.style.display === 'none') {
      logData();
    } else {
      observeUntilEventObserved(
        spinnerOverlay,
        { attributes: true },
        (observer) => {
          logData();
          observer.disconnect();
          return;
        }
      );
    }
  };

  endCustomCPLTracking = () => {
    const page = this.state.isProgressive
        ? 'main:onbrd:signup:donate:guestFormComponent'
        : 'main:donate:wps:guest:guestform:guestFormComponent:web:',
      action = this.state.isProgressive
        ? 'tesla_form_loaded'
        : 'guest_form_loaded',
      uicomp = this.state.isProgressive ? 'teslaForm' : 'guestForm';

    if (
      document.referrer.includes('token=') ||
      performance.navigation.type === performance.navigation.TYPE_RELOAD
    ) {
      // on page refresh or country change
      window.onload = () => {
        setTimeout(() => {
          // Without these, loadEventStart and loadEventEnd would be 0
          // and thus tt would be a negative value
          // We need this because componentDidMount runs before
          // the page has completely loaded all content
          this.logCPLData(() => {
            cpl.logPerformance({
              page,
              action,
              uicomp,
              uitype: 'page',
            });
          });
        }, 0);
      };
    } else {
      this.logCPLData(() => {
        window.PAYPAL.analytics.endCPLTracking({
          page,
          action,
          uicomp,
          uitype: 'component',
        });
      });
    }
  };

  componentDidMount() {
    this.props.toggleSpinner(this.state.isProgressive, this.state.isDonating);
    this.setShouldShowAuCrsBanner();

    // Only adding page name, if "fptPageName=landing", in other cases track event will be triggered when template is loaded.
    if (templateData.fptPageName.toLowerCase().includes('landing')) {
      FPT.trackPageWithData(
        'main:donate:wps:guest:guestform',
        'main:donate:wps:guest:guestform:web::',
        {
          ...(isNna(this.state.country) ? { offer_type: 'nna' } : {}),
        }
      );
    }

    // Checking for recurring status
    if (AppStore.getDonationRecurring() === 'checked') {
      this.setState({ recurringStatus: true }); // eslint-disable-line
    }

    // Checking token status
    handleTokenReuse();

    // Register for App Dispatcher Messages
    this.registerAppDispatcherMessages();

    // Show language selector in the footer
    DonateAppActions.showLanguageSelector(true);

    // startCPLTracking is initiated in landing page
    this.endCustomCPLTracking();
  }

  registerAppDispatcherMessages = () => {
    this.dispatcherMessages = AppDispatcher.register((payload) => {
      const actionType = _get(payload, 'action.actionType');

      if (actionType === 'CHANGE_AMOUNT') {
        const amount = _get(payload, 'action.item.amount', 0);
        this.updateAmount(amount);
      }
      if (actionType === AppConstants.CHANGE_RECURRING) {
        const recurringStatus =
          _get(payload, 'action.item.recurring', '') === 'checked';
        this.setState({ recurringStatus: recurringStatus });
      }
    });
  };

  getGiftAidExtraAmount(amount) {
    const value = (donateGiftPercentage * normalizeNumber(amount)).toFixed(2);
    const currency = AppStore.getDonationCode();
    return formatCurrency({ value, currency });
  }

  updateGiftAidComponent = (state) => {
    const currency = AppStore.getDonationSymbol();
    const amount = AppStore.getDonationAmount() || 0;
    const extraCostText = getContent('locals')('common/giftAidIt')(
      'giftAidIt.extraCost',
      {
        currency,
        amount,
        extraAmount: this.getGiftAidExtraAmount(amount),
      },
      {
        useHTML: true,
      }
    );

    let modalp1 = (giftAidTextObj && giftAidTextObj.modalp1) || '',
      modalp2 = (giftAidTextObj && giftAidTextObj.modalp2) || '',
      applyGiftAid = (giftAidTextObj && giftAidTextObj.applyGiftAid) || '';

    modalp1 = modalp1 && modalp1.replace('{orgName}', state.subHeaderLabel);
    modalp2 = modalp2 && modalp2.replace('{orgName}', state.subHeaderLabel);
    applyGiftAid =
      applyGiftAid && applyGiftAid.replace('{orgName}', state.subHeaderLabel);

    giftAidTextObj.extraCost = extraCostText;
    giftAidTextObj.modalp1 = modalp1;
    giftAidTextObj.modalp2 = modalp2;
    giftAidTextObj.applyGiftAid = applyGiftAid;
  };

  componentWillUnmount() {
    AppDispatcher.unregister(this.dispatcherMessages);
  }

  getFPTDATA = () => {
    return {
      amount: AppStore.getDonationAmount(),
      code: AppStore.getDonationCode(),
      symbol: AppStore.getDonationSymbol(),
    };
  };

  updateConversionStatus = (status = false) => {
    this.setState({ donateDisabled: status });
  };

  handleLogIn = (event) => {
    event.preventDefault();

    if (AppStore.getDonationAmount()) {
      FPT.trackLinkClickWithData('guestLogIn', {
        amount: AppStore.getDonationAmount(),
        code: AppStore.getDonationCode(),
        symbol: AppStore.getDonationSymbol(),
      });
    }

    // Proceed to Login only if the donation amount is valid
    if (this.validateDonationAmount()) {
      handleLogIn();
    }
  };

  /**
   * Display validation error
   */
  displayValidationError = () => {
    const errorMessage = amountUtils.getMinimumDonationErrorMessage(messages);
    this.setState({
      showValidationError: true,
      validationErrorMessage: errorMessage,
    });
    addShake();
  };

  hideValidationError = () => {
    this.setState({ showValidationError: false });
  };

  /**
   * Validates donation amount and displays the validation error if any
   */
  validateDonationAmount = () => {
    let validationResult = amountUtils.validateMinimumDonationAmount(messages);
    if (validationResult?.showValidationError) {
      this.setState(validationResult);
      addShake();
      scrollElementIntoView('.money_input');
      return false;
    }

    validationResult = amountUtils.validateMaximumDonationAmount();
    if (validationResult?.showValidationError) {
      this.setState(validationResult);
      addShake();
      scrollElementIntoView('.money_input');
      return false;
    }

    this.setState(validationResult);
    return true;
  };

  handleRecurringChange = (status = false) => {
    DonateAppActions.changeRecurring({
      recurring: status ? 'checked' : 'unchecked',
    });
  };

  /**
   * Clear the validation error onFocus
   */
  handleAmountFocus = () => {
    if (this.state.showValidationError) {
      this.hideValidationError();
    }
  };

  /*
   * handlePaymentSuccess
   */
  handlePaymentSuccess = (response) => {
    const { token } = templateData;
    const queryParams = objectOrArrayToQueryString({ token });
    if (response.isOfacPending) {
      return this.props.history.push(`/donate/guest/pending?${queryParams}`);
    }
    return this.props.history.push(`/donate/guest/confirmation?${queryParams}`);
  };

  /*
   * handlePaymentFailure
   */
  handlePaymentFailure = () => {
    this.props.toggleSpinner(false);
    return this.props.history.push('/donate/error/payment');
  };

  /*
   * handleMemberFailure
   * Redirect all member failure to donate/member
   * Known case
   * Case 1 :- WSM_PAYER_FUNDING_INSTRUMENTS_UNAVAILABLE
   * Case 2 :- WSM_VALIDATION_ERROR
   */
  handleMemberFailure = () => {
    updateDonation(`/donate/member?token=${templateData.token}`);
  };

  updateAmount(amount) {
    const currency = AppStore.getDonationCode() || '';
    this.setState({
      formattedAmount: formatAmount({ currency, value: amount }),
    });
  }

  handleGiftAidOption(giftAidState) {
    DonateAppActions.updateGiftaidItFlag({ giftAidItFlag: giftAidState });
  }

  /**
   * Should show note when charityNote is enabled and recurring is disabled for non-client flows
   * @return {Boolean} true to show note field
   */
  shouldShowNote = () => {
    return (
      templateData.charityNoteEnabled &&
      !this.state.recurringStatus &&
      !isExternalFlow()
    );
  };

  createMarkup = (str) => {
    return {
      __html: str,
    };
  };

  getPersonalizedContent = () => {
    return getPersonalizedMessages(PPGF_DISCLAIMER_TOUCHPOINT, { merge: true });
  };

  setShouldShowAuCrsBanner = () => {
    const status = getParameterByName('status'),
      statusCode = getParameterByName('statusCode');

    const { auTax: { abortedBanner = '' } = {} } = auCrsMessages;

    if (isCrsAborted(status, statusCode)) {
      this.setState({
        showAuCrsBanner: true,
        auCrsBannerMsg: abortedBanner,
      });
      FPT.trackAuCrs(status, statusCode);
    }
  };

  render() {
    const recurringStatus = AppStore.getDonationRecurring() === 'checked';

    if (templateData.isGiftaiditEnabled) {
      this.updateGiftAidComponent(this.state);
    }

    return (
      <>
        <div
          className={`${
            templateData.bannerUrl ? 'gn_donate_wrapper' : 'donate_wrapper'
          } ${isMobileOrInContext() ? 'mobile_view' : ''} ${
            isInContext() ? 'context_gn_wrapper' : ''
          }`}
        >
          <ServerErrors />
          {isExternalFlow() && templateData.ismobile && <CloseButton />}
          {(isInContext() || isExternalFlow()) && <BackButton />}
          {this.state.showAuCrsBanner && (
            <Alert type="info" id="au_crs_info_banner">
              {this.state.auCrsBannerMsg}
            </Alert>
          )}
          <DonateHeader />
          <div className="container">
            <form
              id="guestForm"
              className={`${
                isExternalFlow() ? 'gn_donate_guest_form' : 'donate_guest_form'
              }`}
              noValidate
            >
              <AmountWrapper
                conversionStatus={this.updateConversionStatus}
                validateDonationAmount={this.validateDonationAmount}
                showValidationError={this.state.showValidationError}
                hideValidationError={this.hideValidationError}
                validationErrorMessage={this.state.validationErrorMessage}
                onFocus={this.handleAmountFocus}
              />

              {this.state.recurringEnabled && (
                <RecurringCheckBox
                  toolTipText={this.state.recurringHelpText}
                  label={this.state.recurringLabel}
                  status={this.state.recurringStatus}
                  handleChange={this.handleRecurringChange}
                  hasRecurringHelp={false}
                  showNote={true}
                />
              )}

              {templateData.isGiftaiditEnabled && (
                <DonateGiftAid
                  isAlreadyEnrolled={false}
                  showAddress={false}
                  addressData="" // eslint-disable-line
                  isChecked={false}
                  handleGiftAidOption={this.handleGiftAidOption}
                  contentMsg={giftAidTextObj}
                />
              )}
              <LinnerDiv />
              {this.shouldShowNote() ? <Note /> : null}
              <Divider
                secondary
                style={{
                  maxWidth: '400px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              />
              <PayCreditOrDebit />

              {!this.state.isProgressive && (
                <div className="donate_guest_form">
                  <GuestForm
                    recurringStatus={recurringStatus}
                    donateDisabled={this.state.donateDisabled}
                    validateDonationAmount={this.validateDonationAmount}
                  />
                </div>
              )}
            </form>
            {this.state.isProgressive && (
              <Suspense fallback={<FallbackSpinner />}>
                <TeslaFormContainer
                  displayValidationError={this.displayValidationError}
                  validateDonationAmount={this.validateDonationAmount}
                  handlePaymentSuccess={this.handlePaymentSuccess}
                  handlePaymentFailure={this.handlePaymentFailure}
                  handleMemberFailure={this.handleMemberFailure}
                />
              </Suspense>
            )}
            {this.state.hasDisclaimer && (
              <Disclaimer content={this.getPersonalizedContent()} />
            )}
          </div>
        </div>
        <CancelAndReturn />
      </>
    );
  }
}

Guest.propTypes = {
  toggleSpinner: PropTypes.func,
  history: PropTypes.object,
  showErrorOnLoad: PropTypes.func,
};

export default withRouter(withDonate(Guest));
